/**
 * Family at a glance (§22.16) — every child + device-health status on
 * the dashboard. Calm by default: healthy kids get a green "Online" chip
 * so the parent has a positive signal that the system is working, instead
 * of "no card = silence". Was previously hidden when everyone was healthy,
 * which made parents wonder where the panel went.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BatteryLow, BatteryCharging, WifiOff, Clock, Smartphone, Wifi, Home, School, Briefcase, MapPin } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { locationsApi, type DeviceHealth } from '@/api/locations';
import { usersApi } from '@/api/users';
import { ChildAvatar } from '@/components/ChildAvatar';

/** Great-circle distance in metres between two lat/lng points. Mirrors the
 *  haversine the map page uses for the same "At/Near {place}" detection
 *  so this card and the map agree on what counts as "at home". */
function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_008.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

const PLACE_ICON = {
  home: Home,
  school: School,
  workplace: Briefcase,
  place: MapPin,
} as const;

type Row = {
  child_id: string;
  state: DeviceHealth['state'] | 'unpaired';
  message: string;
  battery_pct: number | null;
  place_kind?: 'at' | 'near' | null;
  place_name?: string | null;
  place_icon?: keyof typeof PLACE_ICON | null;
};

const STATE_STYLE: Record<Row['state'], { chip: string; Icon: typeof BatteryLow; label: string }> = {
  healthy: { chip: 'bg-emerald-50 text-emerald-700', Icon: Wifi, label: 'Online' },
  low_battery: { chip: 'bg-amber-50 text-amber-800', Icon: BatteryLow, label: 'Low battery' },
  stale: { chip: 'bg-amber-50 text-amber-800', Icon: Clock, label: 'Stale' },
  offline: { chip: 'bg-rose-50 text-rose-700', Icon: WifiOff, label: 'Offline' },
  unpaired: { chip: 'bg-slate-100 text-slate-700', Icon: Smartphone, label: 'Not paired' },
};

export function DeviceHealthCard() {
  const health = useQuery({
    queryKey: ['device-health'],
    queryFn: () => locationsApi.deviceHealth(),
    refetchInterval: 60_000,
    retry: false,
  });
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren(), retry: false });
  // Same react-query key the map page uses, so this fetch is cached/shared.
  // Drives the "at Home / near School" chip per child.
  const family = useQuery({
    queryKey: ['family-current'],
    queryFn: () => locationsApi.familyCurrent(),
    refetchInterval: 60_000,
    retry: false,
  });
  const childById = useMemo(
    () => new Map((children.data ?? []).map((c) => [c.id, c])),
    [children.data],
  );
  const healthByChild = useMemo(
    () => new Map((health.data ?? []).map((d) => [d.child_id, d])),
    [health.data],
  );
  // Freshest position per child — matches the marker / sidebar logic on
  // the map page. Older pings would otherwise misreport "at Home" when
  // the kid has already left.
  const freshestPosByChild = useMemo(() => {
    const out = new Map<string, { lat: number; lng: number; freshness_seconds: number }>();
    for (const p of family.data?.positions ?? []) {
      const existing = out.get(p.child_id);
      if (!existing || p.freshness_seconds < existing.freshness_seconds) {
        out.set(p.child_id, { lat: p.lat, lng: p.lng, freshness_seconds: p.freshness_seconds });
      }
    }
    return out;
  }, [family.data?.positions]);

  // One row per child in the roster. When a child has no DeviceHealth
  // record we synthesize an "unpaired" state so the parent still sees the
  // child here (instead of wondering why they're missing).
  const rows: Row[] = (children.data ?? []).map((c) => {
    const d = healthByChild.get(c.id);
    const pos = freshestPosByChild.get(c.id);
    let place_kind: 'at' | 'near' | null = null;
    let place_name: string | null = null;
    let place_icon: keyof typeof PLACE_ICON | null = null;
    if (pos) {
      // Re-use the same haversine inside/near classification as the map.
      // 120 m grace beyond the geofence radius counts as "Near {place}".
      let inside: { name: string; icon: keyof typeof PLACE_ICON } | null = null;
      let near: { name: string; icon: keyof typeof PLACE_ICON; meters: number } | null = null;
      for (const g of family.data?.geofences ?? []) {
        const dist = haversineMeters(pos.lat, pos.lng, g.center_lat, g.center_lng);
        const kind = (['home', 'school', 'workplace', 'place'].includes(g.icon ?? '')
          ? (g.icon as keyof typeof PLACE_ICON)
          : 'place');
        if (dist <= g.radius_m) {
          inside = { name: g.name, icon: kind };
          break;
        }
        if (dist <= g.radius_m + 120) {
          if (!near || dist - g.radius_m < near.meters) {
            near = { name: g.name, icon: kind, meters: dist - g.radius_m };
          }
        }
      }
      if (inside) { place_kind = 'at'; place_name = inside.name; place_icon = inside.icon; }
      else if (near) { place_kind = 'near'; place_name = near.name; place_icon = near.icon; }
    }
    if (!d) {
      return {
        child_id: c.id,
        state: 'unpaired',
        message: 'No device paired yet.',
        battery_pct: null,
      };
    }
    return {
      child_id: c.id,
      state: d.state,
      message: d.message,
      battery_pct: d.battery_pct ?? null,
      place_kind,
      place_name,
      place_icon,
    };
  });
  if (rows.length === 0) return null;

  // Worst state wins for the card border so "everyone's fine" reads green
  // and "one kid offline" reads red without needing to scan every row.
  const worst: Row['state'] = rows.some((r) => r.state === 'offline')
    ? 'offline'
    : rows.some((r) => r.state === 'stale' || r.state === 'low_battery')
      ? 'stale'
      : rows.some((r) => r.state === 'unpaired')
        ? 'unpaired'
        : 'healthy';
  const cardTint =
    worst === 'offline'
      ? 'border-rose-200 bg-rose-50/40'
      : worst === 'stale'
        ? 'border-amber-200 bg-amber-50/40'
        : worst === 'unpaired'
          ? 'border-slate-200 bg-slate-50/40'
          : 'border-emerald-200 bg-emerald-50/40';

  return (
    <Card className={cardTint}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold">Family right now</p>
          <Link to="/children" className="text-xs font-medium text-[color:var(--kz-color-ink-secondary)] underline-offset-2 hover:underline">
            Manage
          </Link>
        </div>
        <div className="mt-3 grid gap-2">
          {rows.map((r) => {
            const child = childById.get(r.child_id);
            const style = STATE_STYLE[r.state];
            return (
              <div key={r.child_id} className="flex items-center gap-3 rounded-card bg-white/70 p-2.5">
                <ChildAvatar
                  name={child?.name ?? '?'}
                  color={child?.color ?? null}
                  avatarUrl={child?.avatar_url ?? null}
                  size="size-9"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-medium">{child?.name ?? 'Child'}</p>
                    {r.place_kind && r.place_name && (() => {
                      const isAt = r.place_kind === 'at';
                      const PlaceIcon = r.place_icon ? PLACE_ICON[r.place_icon] : MapPin;
                      return (
                        <span
                          className={cn(
                            'inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                            isAt ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800',
                          )}
                          title={isAt ? `Inside ${r.place_name}` : `Near ${r.place_name}`}
                        >
                          <PlaceIcon className="size-2.5" />
                          {isAt ? 'At' : 'Near'} {r.place_name}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="truncate text-xs text-[color:var(--kz-color-ink-muted)]">{r.message}</p>
                </div>
                {typeof r.battery_pct === 'number' && r.state !== 'unpaired' && (
                  <span className="hidden shrink-0 items-center gap-1 text-[11px] text-[color:var(--kz-color-ink-secondary)] sm:inline-flex">
                    <BatteryCharging className="size-3" />
                    {r.battery_pct}%
                  </span>
                )}
                <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium', style.chip)}>
                  <style.Icon className="size-3" />
                  {style.label}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
